import type { Identifier } from "ra-core";

import type { Deal, DealStage, Project } from "../types";
import { isPosiComplete } from "../deals/posi";
import type { DirectSale } from "../incentives/directSales";
import { sumDirectSalesAmountMxn } from "../incentives/directSales";

/**
 * Almikia Epic E7 (TASK-094), generalized to any number of stages by Epic EP
 * (TASK-107): the Dirección panel's pipeline totals and POSI completeness
 * percentage -- pure calculations shared by `PipelineTotalsSection.tsx`
 * (presentation) and `DireccionPanel.tsx` (data fetching). `deals` is always
 * a caller-scoped, non-archived lead list -- RLS (Supabase) or FakeRest's
 * unfiltered demo read already narrows the rows to what the signed-in role
 * may see (direccion: everyone; a lead_manager: her own brazo), so nothing
 * here re-applies role scoping.
 *
 * NFR ("stage totals use count queries, not full lead lists"): each stage's
 * `count` is exact -- `DireccionPanel.tsx`'s `useStageOpenLeadCounts` runs
 * one `perPage: 1` count-only query per EFFECTIVE stage (DATA CONTRACT EP
 * (8): the 10 system stages plus any direccion-created one) and reads its
 * `total`, never `data.length`. `amountMxn` still comes from ONE bounded read
 * (`DireccionPanel.tsx`'s `MAX_OPEN_LEADS`, mirroring the same "generous
 * fixed cap" tradeoff `useAdvisorProgress.ts`/`LiderDashboard.tsx` already
 * use elsewhere in this epic), since no `deals` aggregate view/RPC exists yet
 * to sum MXN per stage server-side (no E7/EP ticket except TASK-101 may touch
 * `supabase/`) -- `mergeExactStageCounts` below overlays the exact counts
 * onto the capped-read totals. The caller surfaces a visible "partial data"
 * note for the MXN figures (and the POSI percentage, which reads the same
 * bounded list) whenever that read's `total` exceeds the rows actually
 * returned.
 *
 * See adr/ADR-ee2f59a9-TASK-094-pipeline-totals-bounded-read.md
 */

export interface PipelineStageTotal {
  /** The effective stage's `value` (DATA CONTRACT EP (8)), not just an E2 key. */
  stage: string;
  count: number;
  amountMxn: number;
}

/**
 * Buckets `deals` into `stages` (the effective stage list, DATA CONTRACT EP
 * (8), in configured order), one total per stage. A deal whose stage matches
 * none of `stages` (a legacy/unknown value) is dropped, mirroring
 * `partitionDealsByStages` (`deals/stages.ts`) -- never a fallback bucket.
 */
export function buildPipelineTotals(
  deals: Array<Pick<Deal, "stage" | "amount">>,
  stages: ReadonlyArray<Pick<DealStage, "value">>,
): PipelineStageTotal[] {
  return stages.map(({ value: stage }) => {
    const stageDeals = deals.filter((deal) => deal.stage === stage);
    return {
      stage,
      count: stageDeals.length,
      amountMxn: stageDeals.reduce(
        (total, deal) => total + (deal.amount ?? 0),
        0,
      ),
    };
  });
}

/**
 * DATA CONTRACT EP acceptance criteria: an archived custom stage is shown
 * only while it still holds at least one open lead -- a system stage is
 * never archivable (`enforce_deal_stage_configs_guard`) so this only ever
 * hides an empty, archived, direccion-created stage. Filters `totals` (after
 * `mergeExactStageCounts`, so `count` is the exact figure the decision must
 * use) against `stages`' `archived` flag.
 */
export function selectDisplayedPipelineTotals(
  totals: readonly PipelineStageTotal[],
  stages: ReadonlyArray<Pick<DealStage, "value" | "archived">>,
): PipelineStageTotal[] {
  const archivedByValue = new Map(
    stages.map((stage) => [stage.value, stage.archived ?? false]),
  );
  return totals.filter(
    (total) => !archivedByValue.get(total.stage) || total.count > 0,
  );
}

/**
 * Percentage (0-100, rounded) of `deals` with all 4 POSI answers complete
 * (`isPosiComplete`, `deals/posi.ts`). `0` when `deals` is empty -- never
 * `NaN` or a divide-by-zero.
 */
export function computePosiCompletePercent(
  deals: Array<Parameters<typeof isPosiComplete>[0]>,
): number {
  if (deals.length === 0) return 0;
  const completeCount = deals.filter((deal) => isPosiComplete(deal)).length;
  return Math.round((completeCount / deals.length) * 100);
}

/**
 * Overlays exact per-stage open-lead counts (from `useStageOpenLeadCounts`'s
 * `perPage: 1` count queries) onto `totals` (from `buildPipelineTotals`,
 * whose `count` came from the same capped read as `amountMxn`) -- `amountMxn`
 * is left untouched, only `count` is replaced. A stage missing from
 * `exactCounts` (e.g. its count query is still pending) keeps its
 * capped-read count rather than silently zeroing it out.
 */
export function mergeExactStageCounts(
  totals: PipelineStageTotal[],
  exactCounts: Partial<Record<string, number>>,
): PipelineStageTotal[] {
  return totals.map((total) =>
    exactCounts[total.stage] != null
      ? { ...total, count: exactCounts[total.stage]! }
      : total,
  );
}

export interface ProjectSalesTotal {
  projectId: Identifier;
  projectName: string;
  count: number;
  amountMxn: number;
}

/**
 * "Proyectos - ventas del mes": current-month direct sales per non-archived
 * project (ticket ASSUMPTION: no project goal is modeled, so no target
 * line). `monthDirectSales` is already period-filtered
 * (`selectDirectSalesInPeriod`, `incentives/directSales.ts`) -- a sale whose
 * `project_id` does not match any of `projects` (e.g. no project assigned,
 * or the project was archived after the sale) is silently excluded from
 * every row, never invented into one.
 */
export function buildProjectSales(
  projects: Array<Pick<Project, "id" | "name">>,
  monthDirectSales: DirectSale[],
): ProjectSalesTotal[] {
  return projects.map((project) => {
    const sales = monthDirectSales.filter(
      (sale) => sale.deal.project_id === project.id,
    );
    return {
      projectId: project.id,
      projectName: project.name,
      count: sales.length,
      amountMxn: sumDirectSalesAmountMxn(sales),
    };
  });
}
